import { css } from '@emotion/react'
import { memo, useCallback } from 'react'
import { openParticipantsDrawer } from '../../../../state/modals/state.drawer'
import { toggleSidebarType } from '../../../../state/state.sidebar'
import { useRoomSelector } from '../../../Hooks/useRoomAppState'
import { useSidebarSelector } from '../../../Hooks/useSidebar'
import AccountIcon from '../../../Icons/Account'
import { getThemeSpacing } from '../../../styles/appTheme'
import { palette } from '../../../styles/colors'
import shape from '../../../styles/shape'
import { PAPER_HEIGHTS } from '../../../theme'

const viewerCountRootStyles = css({
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    zIndex: 24,
    right: getThemeSpacing(2),
    padding: '7px 12px',
    borderRadius: shape.borderRadiusLarge,
    background: 'rgba(0,0,0,0.85)',
    color: palette.commonWhite,
    fontSize: '0.725rem',
    lineHeight: '1.25em',
    cursor: 'pointer',
    '& svg': {
        marginLeft: getThemeSpacing(0.5),
        color: palette.commonWhite,
    },
})

const floatingViewerCountStyles = css({
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    zIndex: PAPER_HEIGHTS.floatingActionBar,
    left: 'calc(env(safe-area-inset-left, 0px) * 0.7)',
    padding: '10px 10px',
    borderBottomRightRadius: shape.borderRadius,
    color: palette.commonWhite,
    fontSize: 13,
    lineHeight: '1.25em',
    filter: palette.overlayFilterDropShadow,
    cursor: 'pointer',
    pointerEvents: 'all',
    '& svg': {
        marginRight: getThemeSpacing(0.5),
        color: palette.commonWhite,
    },
})

interface ViewerCountProps {
    fullWindow?: boolean
    fullWindowPushDown?: number
    id: number
    pinned?: boolean
    pushDown?: boolean
}

const ViewerCount = memo((props: ViewerCountProps) => {
    const { fullWindow, fullWindowPushDown, id, pinned, pushDown } = props
    const sidebarOpen = useSidebarSelector('show')
    const guestCount = useRoomSelector(id, 'guestCount') ?? 0
    const participants = useRoomSelector(id, 'participants') ?? []
    const viewerCount = participants.length + guestCount
    const rootStyles = fullWindow
        ? floatingViewerCountStyles
        : viewerCountRootStyles

    const handleOnClick = useCallback(() => {
        if (sidebarOpen && !pinned && !fullWindow) {
            toggleSidebarType()
        } else {
            openParticipantsDrawer(id)
        }
    }, [fullWindow, id, pinned, sidebarOpen])

    if (viewerCount === undefined) return

    const pushDownTopCalc = pushDown ? 40 : fullWindowPushDown
    return (
        <div
            onClick={handleOnClick}
            data-testid="Chat__Participants"
            title="Participants"
            css={rootStyles}
            style={
                fullWindow
                    ? {
                          top: `calc(env(safe-area-inset-top, 0px) * 0.7 + ${pushDownTopCalc ?? 0}px)`,
                      }
                    : { top: getThemeSpacing(2, true) + (pushDownTopCalc ?? 0) }
            }
        >
            {!fullWindow && <span>{String(viewerCount)}</span>}
            <AccountIcon size={'1.25em'} />
            {fullWindow && <span>{String(viewerCount)}</span>}
        </div>
    )
})

export default ViewerCount
